import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";

export const ACCESS_TOKEN_KEY = "entra_access_token";

/**
 * Hook to access the access token from sessionStorage.
 * AuthBridge stores the token on login.
 */
export function useAccessToken(): string | null {
  const { instance, accounts } = useMsal();
  const [token, setToken] = useState<string | null>(() => {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  });

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY) {
        setToken(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const checkAndRefreshToken = async () => {
      const account = accounts[0];
      if (!account) {
        setToken(null);
        return;
      }

      const storedToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      if (storedToken) {
        setToken(storedToken);
        return;
      }

      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["User.Read", "openid", "profile", "email"],
          account: account,
        });

        if (response?.accessToken) {
          sessionStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          setToken(response.accessToken);
        }
      } catch (error) {
        console.error("Failed to acquire access token:", error);
        setToken(null);
      }
    };

    checkAndRefreshToken();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [instance, accounts]);

  return token;
}

export function getAccessTokenFromStorage(): string | null {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAccessTokenInStorage(token: string): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function removeAccessTokenFromStorage(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
}
